import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, signal } from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute } from '@angular/router';
import { Attendee, Event, OptionGroup, OptionItem, Registration } from '../../../domain/registrator';
import { EventService } from '../../../services/event.service';
import { RegistrationService } from '../../../services/registration.service';

interface OptionTally {
  item: OptionItem;
  count: number;
}

interface GroupTally {
  group: OptionGroup;
  items: OptionTally[];
  totalSelections: number;
}

/**
 * Shows, for each option group of the event, how many attendees selected each
 * option item. Computed client-side from the event registrations
 * (GET /ipa/events/{shortName}/registrations), whose attendees carry
 * `preferences: { [optionGroupId]: optionItemId[] }`.
 */
@Component({
  selector: 'app-event-preferences',
  imports: [
    CommonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatTooltipModule
  ],
  templateUrl: './event-preferences.component.html',
  styleUrl: './event-preferences.component.scss'
})
export class EventPreferencesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly eventService = inject(EventService);
  private readonly registrationService = inject(RegistrationService);

  event: Event = this.route.parent!.snapshot.data['event'];
  loading = signal<boolean>(true);
  totalAttendees = 0;
  groupTallies: GroupTally[] = [];

  ngOnInit() {
    const eventShortName = this.route.parent!.snapshot.paramMap.get('eventname');
    if (!eventShortName) {
      this.loading.set(false);
      return;
    }
    // re-fetch the event so option groups are fresh (same as event-detail)
    this.eventService.getEventByName(eventShortName).subscribe(event => {
      if (event) {
        this.event = event;
      }
      this.loadSummary();
    });
  }

  private loadSummary() {
    this.registrationService.getEventRegistrations(this.event.shortName, undefined).subscribe({
      next: (registrations: Registration[]) => {
        const attendees: Attendee[] = (registrations ?? []).flatMap(r => r.attendees ?? []);
        this.totalAttendees = attendees.length;
        this.groupTallies = (this.event.optionGroups ?? [])
          .slice()
          .sort((a, b) => a.priority - b.priority)
          .map(group => {
            const items: OptionTally[] = (group.optionItems ?? [])
              .slice()
              .sort((a, b) => a.priority - b.priority)
              .map(item => ({
                item,
                count: attendees.filter(a => (a.preferences?.[group.id] ?? []).includes(item.id)).length
              }));
            return { group, items, totalSelections: items.reduce((sum, t) => sum + t.count, 0) };
          });
        this.loading.set(false);
      },
      error: (err) => {
        console.error('failed to load registrations for the preference summary', err);
        this.loading.set(false);
      }
    });
  }

  pct(count: number): number {
    return this.totalAttendees > 0 ? Math.round((100 * count) / this.totalAttendees) : 0;
  }
}
